import { useEffect, useState, useRef } from "react";
import useTypewriterStore from "../stores/useTypewriterStore";

/**
 * Хук для typewriter анімації тексту
 * @param text - Текст для анімації
 * @returns Відображений текст (поступово збільшується)
 */
export function useTypewriter(text: string | undefined): string {
    const [displayedText, setDisplayedText] = useState("");
    const typewriterStore = useTypewriterStore();
    const timeoutRef = useRef<number | null>(null);
    const currentIndexRef = useRef(0);
    const previousTextRef = useRef("");
    const isMountedRef = useRef(false);
    // undefined sentinel so the first non-undefined text always triggers the reset path
    const [prevText, setPrevText] = useState<string | undefined>(undefined);

    // (b) Reset visible prefix when source text changes — during render, not in effect
    if (text !== prevText) {
        setPrevText(text);
        const fullText = text || "";
        if (!fullText) {
            setDisplayedText("");
        } else if (fullText.length <= 1) {
            setDisplayedText(fullText);
        } else {
            setDisplayedText(fullText.slice(0, 1));
        }
    }

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, []);

    // (c) Timer-driven typewriter ticks — setState only inside setTimeout callbacks
    useEffect(() => {
        const fullText = text || "";
        const delay = typewriterStore.delay;

        if (fullText !== previousTextRef.current) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            previousTextRef.current = fullText;

            if (!fullText) {
                typewriterStore.end();
                return;
            }

            if (fullText.length <= 1) {
                typewriterStore.end();
                return;
            }

            currentIndexRef.current = 1;
            typewriterStore.start();

            const startAnimation = () => {
                if (!isMountedRef.current) {
                    return;
                }

                const animate = () => {
                    if (!isMountedRef.current || fullText !== previousTextRef.current) {
                        typewriterStore.end();
                        return;
                    }

                    if (currentIndexRef.current < fullText.length) {
                        currentIndexRef.current += 1;
                        setDisplayedText(fullText.slice(0, currentIndexRef.current));

                        if (currentIndexRef.current < fullText.length) {
                            timeoutRef.current = window.setTimeout(animate, delay);
                        } else {
                            typewriterStore.end();
                            timeoutRef.current = null;
                        }
                    } else {
                        typewriterStore.end();
                        timeoutRef.current = null;
                    }
                };

                timeoutRef.current = window.setTimeout(animate, delay);
            };

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (isMountedRef.current) {
                        startAnimation();
                    }
                });
            });
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [text, typewriterStore.delay, typewriterStore]);

    return displayedText;
}
